import { Link, Outlet, useLocation } from "react-router";
import {
  isRouteEnabled,
  paths,
  type RouteId,
} from "@shared-core/routing/routes";
import { useIsAdmin } from "../app/auth";
import { useCanCurateCatalogue } from "../app/auth/hooks";
import {
  useCanManageFeatureSessions,
  useCanManageTicketCategories,
  useCanManageDepartments,
  useCanManageBuddyLists,
  useCanManageEmailCaptureExclusions,
  useCanReadFeatures,
  useCanReadManufacturingSetup,
} from "../app/auth/hooks";
type IntelligencePanel = {
  /** The manifest entry this panel opens — feeds `isRouteEnabled` and, via
   *  `canOpenById` below, the role gate that decides whether it shows. */
  route: RouteId;
  path: string;
  label: string;
};

const intelligencePanels: IntelligencePanel[] = [
  {
    route: "settingsRecommendations",
    path: paths.settingsRecommendations(),
    label: "Recommendations",
  },
  { route: "settingsAlerts", path: paths.settingsAlerts(), label: "Alerts" },
  { route: "settingsSignals", path: paths.settingsSignals(), label: "Signals" },
  {
    route: "settingsBulkDefinitions",
    path: paths.settingsBulkDefinitions(),
    label: "Bulk import",
  },
];

const forecastingPanels: IntelligencePanel[] = [
  {
    route: "settingsPatternDiscovery",
    path: paths.settingsPatternDiscovery(),
    label: "Pattern discovery",
  },
];

// Note `settingsAssignments` is `admin_access`, not `catalogue` — it is
// grouped here for navigation, not by feature. That is exactly why the filter
// below runs per panel: in an environment without `catalogue` this group
// collapses to the remaining panels rather than disappearing and taking a
// production surface with it.
//
// Settings › Catalogue is the curation home for everything catalogue-related
// (product-owner decision, 12 Sep, 03_manufacturing_surfaces_design.md "IA —
// Manufacturing nests under Settings › Catalogue as Live costing"): Dictionary
// moved in from its own top-level `/catalogue/...` URL,
// and Manufacturing folded in, renamed, as the last tab — it exists only as a
// by-product of catalogue live pricing, not a settings area of its own.
const cataloguePanels: IntelligencePanel[] = [
  {
    route: "settingsCatalogues",
    path: paths.settingsCatalogues(),
    label: "Catalogues",
  },
  {
    route: "settingsPricing",
    path: paths.settingsPricing(),
    label: "Price groups",
  },
  {
    route: "settingsPricingTargets",
    path: paths.settingsPricingTargets(),
    label: "Pricing targets",
  },
  {
    route: "settingsPricingReview",
    path: paths.settingsPricingReview(),
    label: "Price review",
  },
  {
    route: "settingsAssignments",
    path: paths.settingsAssignments(),
    label: "Assignments",
  },
  {
    route: "dictionaryAdmin",
    path: paths.dictionaryAdmin(),
    label: "Dictionary",
  },
  {
    route: "settingsLiveCosting",
    path: paths.settingsLiveCosting(),
    label: "Live costing",
  },
];

/**
 * The panels the signed-in user can actually open — feature-enabled
 * (`isRouteEnabled`) AND permitted by their role (`canOpen`). A tab group
 * must link to the first survivor of this filter, not merely the first
 * feature-enabled panel: a manufacturing-setup-only engineer sees the
 * Catalogue tab (it also hosts Live costing) but cannot open "Catalogues",
 * its first-listed panel — landing them there is a dead end, not a tab.
 */
function openPanels(
  panels: IntelligencePanel[],
  canOpen: (route: RouteId) => boolean,
): IntelligencePanel[] {
  return panels.filter(
    (panel) => isRouteEnabled(panel.route) && canOpen(panel.route),
  );
}

function isIntelligenceRoute(pathname: string): boolean {
  return intelligencePanels.some((p) => pathname.startsWith(p.path));
}

function isForecastingRoute(pathname: string): boolean {
  return forecastingPanels.some((p) => pathname.startsWith(p.path));
}

/** Segment-boundary path match: `/settings/pricing` matches itself and
 * `/settings/pricing/x`, but NOT `/settings/pricing-targets`. */
function matchesPath(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

function isCatalogueRoute(pathname: string): boolean {
  return cataloguePanels.some((p) => matchesPath(pathname, p.path));
}

export function SettingsLayout() {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  // Catalogue curation + pricing policy: admin + manager, mirroring the
  // backend's catalogue:manage / pricing:manage and the route guards. The
  // group used to be admin-only in the nav while the pages themselves were
  // (now) open to managers — a manager could reach them only by deep link.
  const canCurateCatalogue = useCanCurateCatalogue();
  const canManageFeatureSessions = useCanManageFeatureSessions();
  const canManageTicketCategories = useCanManageTicketCategories();
  const canManageBuddyLists = useCanManageBuddyLists();
  const canManageDepartments = useCanManageDepartments();
  const canManageEmailCaptureExclusions = useCanManageEmailCaptureExclusions();
  const canReadFeatures = useCanReadFeatures();
  const canReadManufacturingSetup = useCanReadManufacturingSetup();

  // Per-panel role gate, mirroring each panel's own ProtectedRoute tier in
  // app/routes.tsx — used to compute which panel a tab group actually lands
  // on and which sub-tabs it shows, not just whether the group's feature is
  // on. Intelligence/Forecasting happen to share one uniform gate (isAdmin)
  // across every panel; Catalogue does not — only Live costing is open to
  // an engineer, so the group can't be gated (or landed on) as a whole.
  const canOpenById: Partial<Record<RouteId, boolean>> = {
    settingsRecommendations: isAdmin,
    settingsAlerts: isAdmin,
    settingsSignals: isAdmin,
    settingsBulkDefinitions: isAdmin,
    settingsPatternDiscovery: isAdmin,
    settingsCatalogues: canCurateCatalogue,
    settingsPricing: canCurateCatalogue,
    // Its real route guard is commercialTier (admin/manager/commercial); no
    // commercial-role hook exists yet, and before Live costing joined this
    // tab the whole group was gated on canCurateCatalogue anyway, so a
    // commercial-only user already couldn't reach it via this tab — this
    // preserves that pre-existing behaviour rather than changing it.
    settingsPricingTargets: canCurateCatalogue,
    // Matches its route guard (`catalogueCuration`). Without an entry here
    // `canOpen` defaults to true, which showed the tab to every role and
    // then bounced them off the route — and, because the Catalogue group
    // collapses to its single openable panel, stopped an engineer's tab
    // collapsing to Live costing at all.
    settingsPricingReview: canCurateCatalogue,
    settingsAssignments: canCurateCatalogue,
    dictionaryAdmin: canCurateCatalogue,
    settingsLiveCosting: canReadManufacturingSetup,
  };
  const canOpen = (route: RouteId): boolean => canOpenById[route] ?? true;

  const onIntelligence = isIntelligenceRoute(location.pathname);
  const onForecasting = isForecastingRoute(location.pathname);
  const onIntegrations = location.pathname.startsWith(
    paths.settingsIntegrations(),
  );
  const onCatalogue = isCatalogueRoute(location.pathname);
  const onJobs = location.pathname.startsWith(paths.settingsJobs());
  const onUsers = location.pathname.startsWith(paths.settingsUsers());
  const onFeedback = location.pathname.startsWith(paths.settingsFeedback());
  const onWallboard = location.pathname.startsWith(paths.settingsWallboard());
  const onShopfloorKiosk = location.pathname.startsWith(
    paths.settingsShopfloorKiosk(),
  );
  const onTicketCategories = location.pathname.startsWith(
    paths.settingsTicketCategories(),
  );
  const onBuddies = location.pathname.startsWith(paths.settingsBuddies());
  const onDepartments = location.pathname.startsWith(
    paths.settingsDepartments(),
  );
  const onEmailCaptureExclusions = location.pathname.startsWith(
    paths.settingsEmailCaptureExclusions(),
  );
  const onFeatures = location.pathname.startsWith(paths.settingsFeatures());
  const onNeedsAttentionRules = location.pathname.startsWith(
    paths.settingsNeedsAttentionRules(),
  );
  const onCatalogueMigration = location.pathname.startsWith(
    paths.settingsCatalogueMigration(),
  );

  // A tab group shows while it has at least one reachable panel, and points at
  // the first of them. When only one survives, the group IS that panel, so the
  // tab takes its label rather than advertising a section that no longer
  // exists.
  const visibleIntelligencePanels = openPanels(intelligencePanels, canOpen);
  const visibleForecastingPanels = openPanels(forecastingPanels, canOpen);
  const visibleCataloguePanels = openPanels(cataloguePanels, canOpen);
  const groupLabel = (panels: IntelligencePanel[], label: string): string =>
    panels.length === 1 ? panels[0].label : label;

  return (
    <div>
      <div className="border-b border-border mb-6">
        <div className="flex flex-wrap gap-1 pb-2">
          {isRouteEnabled("settingsIntegrations") && (
            <Link
              to={paths.settingsIntegrations()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onIntegrations
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Integrations
            </Link>
          )}
          {visibleIntelligencePanels.length > 0 && (
            <Link
              to={visibleIntelligencePanels[0].path}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onIntelligence
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {groupLabel(visibleIntelligencePanels, "Intelligence")}
            </Link>
          )}
          {visibleForecastingPanels.length > 0 && (
            <Link
              to={visibleForecastingPanels[0].path}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onForecasting
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {groupLabel(visibleForecastingPanels, "Forecasting")}
            </Link>
          )}
          {visibleCataloguePanels.length > 0 && (
            <Link
              to={visibleCataloguePanels[0].path}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onCatalogue
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {groupLabel(visibleCataloguePanels, "Catalogue")}
            </Link>
          )}
          {isAdmin && isRouteEnabled("settingsJobs") && (
            <Link
              to={paths.settingsJobs()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onJobs
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Jobs
            </Link>
          )}
          {isAdmin && isRouteEnabled("settingsUsers") && (
            <Link
              to={paths.settingsUsers()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onUsers
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Manage users
            </Link>
          )}
          {isAdmin && isRouteEnabled("settingsFeedback") && (
            <Link
              to={paths.settingsFeedback()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onFeedback
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Feedback
            </Link>
          )}
          {isAdmin && isRouteEnabled("settingsWallboard") && (
            <Link
              to={paths.settingsWallboard()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onWallboard
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Wallboard
            </Link>
          )}
          {isAdmin && isRouteEnabled("settingsShopfloorKiosk") && (
            <Link
              to={paths.settingsShopfloorKiosk()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onShopfloorKiosk
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Kiosk terminals
            </Link>
          )}
          {isAdmin && isRouteEnabled("settingsCatalogueMigration") && (
            <Link
              to={paths.settingsCatalogueMigration()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onCatalogueMigration
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Catalogue migration
            </Link>
          )}
          {canReadFeatures && isRouteEnabled("settingsFeatures") && (
            <Link
              to={paths.settingsFeatures()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onFeatures
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Features
            </Link>
          )}
          {canManageTicketCategories &&
            isRouteEnabled("settingsTicketCategories") && (
              <Link
                to={paths.settingsTicketCategories()}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  onTicketCategories
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Ticket Categories
              </Link>
            )}
          {canManageBuddyLists && isRouteEnabled("settingsBuddies") && (
            <Link
              to={paths.settingsBuddies()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onBuddies
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Buddies
            </Link>
          )}
          {canManageDepartments && isRouteEnabled("settingsDepartments") && (
            <Link
              to={paths.settingsDepartments()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onDepartments
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Departments
            </Link>
          )}
          {isRouteEnabled("settingsNeedsAttentionRules") && (
            <Link
              to={paths.settingsNeedsAttentionRules()}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                onNeedsAttentionRules
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Needs attention rules
            </Link>
          )}
          {canManageEmailCaptureExclusions &&
            isRouteEnabled("settingsEmailCaptureExclusions") && (
              <Link
                to={paths.settingsEmailCaptureExclusions()}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  onEmailCaptureExclusions
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Email Exclusions
              </Link>
            )}
          {canManageFeatureSessions && isRouteEnabled("featureSessions") && (
            // Links out to the full-bleed /features workspace rather than
            // re-pathing under /settings — the split-pane workspace must not be
            // wrapped in the Settings tab chrome. The Settings tab is just the
            // entry point.
            <Link
              to={paths.featureSessions()}
              className="px-3 py-1.5 rounded-md text-sm transition-colors text-muted-foreground hover:text-foreground"
            >
              Dev sessions
            </Link>
          )}
        </div>
      </div>

      {onIntelligence && visibleIntelligencePanels.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-1">
            {visibleIntelligencePanels.map((panel) => {
              const active = matchesPath(location.pathname, panel.path);
              return (
                <Link
                  key={panel.path}
                  to={panel.path}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    active
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {panel.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {onCatalogue && visibleCataloguePanels.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-1">
            {visibleCataloguePanels.map((panel) => {
              const active = matchesPath(location.pathname, panel.path);
              return (
                <Link
                  key={panel.path}
                  to={panel.path}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    active
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {panel.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {onForecasting && visibleForecastingPanels.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-1">
            {visibleForecastingPanels.map((panel) => {
              const active = matchesPath(location.pathname, panel.path);
              return (
                <Link
                  key={panel.path}
                  to={panel.path}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    active
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {panel.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <Outlet />
    </div>
  );
}
